import { ArrowUp, ArrowDown } from "lucide-react";

/**
 * Subtle visual indicator for the existing week-over-week label string.
 *
 * The label itself is generated by the backend
 * (`_compute_weekly_booking_comparison`) and is one of:
 *   - "+X % vs forrige uke"     → small up-arrow, subtle green
 *   - "-X % vs forrige uke"     → small down-arrow, subtle red
 *   - "Samme som forrige uke"   → plain text, no arrow
 *   - "Ny aktivitet denne uken" → plain text, no arrow
 *   - "Ingen sammenligning ennå"→ plain text, no arrow
 *
 * No backend changes. We parse the prefix of the string the API already
 * returns, so the source of truth for the comparison rule stays in one
 * place on the server.
 *
 * `className` lets the caller override the default text size — the hero
 * overlay uses 11px, the dashboard hint row uses 12px.
 */
export function TrendBadge({ label, className = "" }) {
  if (!label) return null;
  const trimmed = label.trim();
  const isPositive = trimmed.startsWith("+");
  // Accept both ASCII hyphen and Unicode minus sign for robustness.
  const isNegative = trimmed.startsWith("-") || trimmed.startsWith("−");

  if (isPositive) {
    return (
      <span
        className={`inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-400 ${className}`}
        data-testid="trend-badge-up"
      >
        <ArrowUp className="w-3 h-3" strokeWidth={2.25} aria-hidden="true" />
        <span>{trimmed}</span>
      </span>
    );
  }
  if (isNegative) {
    return (
      <span
        className={`inline-flex items-center gap-1 text-rose-700 dark:text-rose-400 ${className}`}
        data-testid="trend-badge-down"
      >
        <ArrowDown className="w-3 h-3" strokeWidth={2.25} aria-hidden="true" />
        <span>{trimmed}</span>
      </span>
    );
  }
  // Neutral states ("Samme som forrige uke", "Ny aktivitet denne uken",
  // "Ingen sammenligning ennå") render as plain muted text, no icon.
  return (
    <span className={className} data-testid="trend-badge-neutral">
      {trimmed}
    </span>
  );
}
